import { ByteString } from "./ByteString.js";
export class InvalidInputError extends Error {
}
export class InvalidRelativeValueError extends Error {
}
export class ByteBuffer {
    timeout;
    static of(...parts) {
        const buffer = new ByteBuffer();
        for (const bytes of parts) {
            buffer.writeSync(bytes);
        }
        buffer.endWrite();
        return buffer;
    }
    static async produceEncoded(val, encodingFunction) {
        const buffer = new ByteBuffer();
        await encodingFunction(val, buffer);
        buffer.endWrite();
        return buffer.readAll();
    }
    static async produceRelativeEncoded(val, rel, encodingFunction) {
        const buffer = new ByteBuffer();
        await encodingFunction(val, rel, buffer);
        buffer.endWrite();
        return buffer.readAll();
    }
    static async produceDecoded(input, decodingFunction, canonic) {
        const buffer = this.of(input);
        const result = await decodingFunction(buffer, canonic);
        buffer.endRead();
        return result;
    }
    static async produceRelativeDecoded(input, rel, decodingFunction, canonic) {
        const buffer = this.of(input);
        const result = await decodingFunction(rel, buffer, canonic);
        buffer.endRead();
        return result;
    }
    buffer = [];
    currentReadPart;
    currentReadPartCursor = 0;
    writtenBytes = 0;
    readBytes = 0;
    readRequest;
    isWriteEnded = false;
    isReadEnded = false;
    stalledWriteTimeout;
    constructor(timeout = 5000) {
        this.timeout = timeout;
    }
    get remainingBytes() {
        return this.writtenBytes - this.readBytes;
    }
    write(bytes) {
        if (typeof bytes === "number") {
            bytes = ByteString.of(bytes);
        }
        this.writeSync(bytes);
        return Promise.resolve();
    }
    writeSync(bytes) {
        clearTimeout(this.stalledWriteTimeout);
        if (this.isWriteEnded) {
            throw new Error("Tried to write after write ended");
        }
        if (this.isReadEnded) {
            throw new Error("Tried to write after read ended");
        }
        this.buffer.push(bytes);
        this.writtenBytes += bytes.length;
        if (this.readRequest && this.remainingBytes >= this.readRequest.length) {
            this.fulfillReadRequest(this.readRequest);
            this.readRequest = undefined;
        }
    }
    async read(length) {
        if (this.isReadEnded) {
            throw new Error("Tried to read after read ended");
        }
        return new Promise((resolve, reject) => {
            const readRequest = {
                length,
                resolve,
                reject,
            };
            if (this.remainingBytes >= length) {
                this.fulfillReadRequest(readRequest);
            }
            else if (this.isWriteEnded) {
                reject(new InvalidInputError("Write is closed and not enough bytes remaining for read request", {
                    cause: {
                        req: length,
                        remaining: this.remainingBytes,
                    },
                }));
            }
            else {
                this.readRequest = {
                    ...readRequest,
                    resolve(value) {
                        if (!ByteString.isOfLength(value, readRequest.length)) {
                            throw new Error("Got ByteString of invalid length, expected " + readRequest.length, {
                                cause: value,
                            });
                        }
                        readRequest.resolve(value);
                    },
                };
                this.stalledWriteTimeout = setTimeout(() => {
                    readRequest.reject(new Error("No bytes were written before timeout."));
                }, this.timeout);
            }
        });
    }
    async readAll(endRead = true) {
        if (this.readBytes > 0) {
            throw new Error("Some bytes were already read");
        }
        if (!this.isWriteEnded) {
            throw new Error("Write is still not done");
        }
        const result = await this.read(this.remainingBytes);
        if (endRead) {
            this.endRead();
        }
        return result;
    }
    fulfillReadRequest(req) {
        if (this.isReadEnded) {
            throw new Error("Tried to read (async) after read ended");
        }
        if (this.remainingBytes < req.length) {
            req.reject(new Error("Tried to read " +
                req.length +
                " bytes with only " +
                this.remainingBytes +
                " bytes remaining."));
            return;
        }
        const outputParts = [];
        let consumedBytes = 0;
        while (consumedBytes < req.length) {
            if (!this.currentReadPart) {
                this.currentReadPart = this.buffer.splice(0, 1).at(0);
                this.currentReadPartCursor = 0;
                if (!this.currentReadPart) {
                    throw new Error("Buffer didn't return an expected ByteString");
                }
            }
            const bytesToRead = Math.min(this.currentReadPart.length - this.currentReadPartCursor, req.length - consumedBytes);
            outputParts.push(this.currentReadPart.slice(this.currentReadPartCursor, this.currentReadPartCursor + bytesToRead));
            this.currentReadPartCursor += bytesToRead;
            consumedBytes += bytesToRead;
            this.readBytes += bytesToRead;
            if (this.currentReadPartCursor === this.currentReadPart.length) {
                this.currentReadPart = undefined;
            }
        }
        const result = ByteString.concat(...outputParts);
        if (!ByteString.isOfLength(result, req.length)) {
            throw new Error("Got invalid read length", {
                cause: { actual: result.length, expected: req.length },
            });
        }
        req.resolve(result);
    }
    endWrite() {
        if (this.isWriteEnded) {
            throw new Error("Write is already ended");
        }
        this.isWriteEnded = true;
        if (this.readRequest) {
            this.readRequest.reject(new InvalidInputError("Write was closed before providing enough bytes to fulfill request for " +
                this.readRequest.length +
                " bytes."));
            throw new Error("Unexpected end of input");
        }
    }
    endRead() {
        if (this.isReadEnded) {
            throw new Error("Read is already ended");
        }
        this.isReadEnded = true;
        if (this.remainingBytes > 0) {
            throw new InvalidInputError("Not all bytes where read", {
                cause: {
                    remaining: this.remainingBytes,
                    written: this.writtenBytes,
                    read: this.readBytes,
                },
            });
        }
    }
}
//# sourceMappingURL=ByteBuffer.js.map